import classNames from "classnames";
import type { ProCardCMSData } from "lib/types/homepage";
import type { FullDatoImage } from "lib/types/datoBlocks";
import type { ArticleShort } from "lib/types/article";
import { InsightsArticleList } from "./insightsArticleList";
import SiftedInsightsFeatureCard from "./siftedInsightsFeatureCard";

export type SiftedInsightsSectionData = {
  sectionTitle?: string;
  featureCards?: ProCardCMSData[];
  featuredArticles?: ArticleShort[];
};

type SiftedInsightsSectionContentProps = {
  data: SiftedInsightsSectionData;
};

export const SiftedInsightsSectionContent = ({
  data,
}: SiftedInsightsSectionContentProps) => {
  const {
    sectionTitle = "Sifted Insights",
    featureCards = [],
    featuredArticles = [],
  } = data;

  // Only show first 2 feature cards for this section
  const displayCards = featureCards.slice(0, 2);

  return (
    <div className="mx-auto w-full max-w-content-mobile px-5 md:max-w-content lg:max-w-container lg:px-0 mdDesktop:max-w-container mdDesktop:px-5">
      {/* Section Header */}
      <div className="mb-4 flex flex-col gap-4">
        <h2 className="text-black-rock font-emphasis-18">{sectionTitle}</h2>
        <div className="h-[1px] w-full bg-mono-20" />
      </div>

      {/* 2-column Grid for tablet, 3-column Grid for large tablet and desktop */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 mdDesktop:grid-cols-[1fr_1fr_258px]">
        {/* Feature Cards - first 2 columns */}
        {displayCards.map((card, index) => {
          // Transform DatoImageType to FullDatoImage
          const backgroundImage: FullDatoImage | undefined = card.cardImage
            ? {
                url: card.cardImage.url,
                alt: card.cardImage.alt || "",
                width: card.cardImage.width || 0,
                height: card.cardImage.height || 0,
                blurUpThumb: card.cardImage.blurUpThumb,
                title: card.cardImage.title || "",
                id: card.cardImage.id || "",
              }
            : undefined;

          return (
            <div
              key={card.id || `feature-card-${index}`}
              className="h-[435px] md:h-[405px]"
            >
              <SiftedInsightsFeatureCard
                title={card.headline}
                excerpt={card.description}
                leftLabel={card.date}
                backgroundImage={backgroundImage}
                href={card.ctaButtonLink}
                buttonText={card.ctaButtonText}
                variant={card.variant}
              />
            </div>
          );
        })}

        {/* Article List - 3rd column (large tablet and desktop) */}
        {featuredArticles.length > 0 && (
          <div className="hidden lg:block mdDesktop:block">
            <InsightsArticleList articles={featuredArticles} />
          </div>
        )}
      </div>

      {/* Article List - full width on mobile/tablet only */}
      {featuredArticles.length > 0 && (
        <div className="w-full md:mx-auto md:max-w-[800px] lg:hidden mdDesktop:hidden">
          <InsightsArticleList articles={featuredArticles} />
        </div>
      )}
    </div>
  );
};

type SiftedInsightsSectionProps = {
  data: SiftedInsightsSectionData;
  className?: string;
};

export const SiftedInsightsSection = ({
  data,
  className,
}: SiftedInsightsSectionProps) => {
  return (
    <section className={classNames("bg-mono-05 pb-16 pt-0", className)}>
      <SiftedInsightsSectionContent data={data} />
    </section>
  );
};
